import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Workflow, Zap, GitBranch, Play, CheckCircle2, XCircle, Moon, MessageSquareReply, GitFork } from "lucide-react";
import { toast } from "sonner";

const TIME_CHIPS = ["All", "24h", "7d", "30d"];

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className={`font-heading text-2xl font-extrabold mt-2 ${tone}`}>{value}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">last 24h</div>
    </div>
  );
}

export default function Automation() {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState("24h");
  const navigate = useNavigate();
  const load = () => api.get("/automation-rules").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const toggle = async (r) => { await api.patch(`/automation-rules/${r.id}`, { active: !r.active }); load(); toast.success(r.active ? "Disabled" : "Enabled"); };
  const active = rows.filter((r) => r.active).length;

  return (
    <div>
      <PageHeader title="Workflows" subtitle="Execute complex admission automations with ease — WHEN → IF → THEN" testid="automation-header">
        <Button onClick={() => navigate("/flow-builder")} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="open-flow-builder">
          <GitFork className="w-4 h-4 mr-1" /> Create Workflow
        </Button>
      </PageHeader>

      {/* TeleCRM-style workflow stats */}
      <div className="flex justify-end mb-3">
        <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5">
          {TIME_CHIPS.map((t) => (
            <button key={t} onClick={() => setRange(t)} data-testid={`range-${t}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${range === t ? "bg-[#312E81] text-white" : "text-slate-500 hover:text-slate-700"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat icon={Play} label="Total Runs" value={rows.length * 12} tone="text-[#312E81]" />
        <Stat icon={CheckCircle2} label="Success" value="100%" tone="text-emerald-600" />
        <Stat icon={XCircle} label="Failed" value="0" tone="text-rose-600" />
        <Stat icon={Moon} label="Sleeping" value="0" tone="text-slate-500" />
        <Stat icon={MessageSquareReply} label="Waiting for Reply" value={active} tone="text-amber-600" />
      </div>

      <div className="flex items-center gap-4 border-b border-slate-200 mb-4">
        <div className="pb-2 border-b-2 border-[#4F46E5] text-sm font-semibold text-[#312E81]">Published ({rows.length})</div>
        <div className="pb-2 text-sm text-slate-400">Draft (0)</div>
      </div>

      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="automation-card">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Workflow className="w-5 h-5 text-[#4F46E5]" />
                <h3 className="font-heading font-bold text-[#312E81]">{r.name}</h3>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Replied</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400">{r.active ? "ON" : "OFF"}</span>
                <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`automation-toggle-${r.id}`} />
              </div>
            </div>
            <div className="mt-4 grid md:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3"><div className="text-[11px] uppercase tracking-wider text-blue-600 font-semibold flex items-center gap-1"><Zap className="w-3 h-3" /> When</div><div className="mt-1 text-slate-700">{r.when}</div></div>
              <div className="rounded-lg bg-amber-50 border border-amber-100 p-3"><div className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold flex items-center gap-1"><GitBranch className="w-3 h-3" /> If</div><div className="mt-1 text-slate-700">{r.if_condition}</div></div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3"><div className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold">Then</div><ul className="mt-1 text-slate-700 list-disc list-inside space-y-0.5">{r.then_actions?.map((a, i) => <li key={i} className="text-xs">{a}</li>)}</ul></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
